import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Collapse, IconButton, Divider } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';

/**
 * Displays the features and associated product options for a given product.
 * Features are rendered as a list of expandable cards.
 */
function OfferingIncludedProducts({ product }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [processedData, setProcessedData] = useState({ features: [], standaloneOptions: [] });

  useEffect(() => {
    if (product && product.related_data) {
      const features = product.related_data.features || [];
      const options = product.related_data.productOptions || [];

      const featureMap = new Map(features.map((f) => [f.sfId || f.id, { ...f, options: [] }]));
      const standaloneOptions = [];

      options.forEach((option) => {
        const featureId = option.fields?.SBQQ__Feature__c;
        if (featureId && featureMap.has(featureId)) {
          featureMap.get(featureId).options.push(option);
        } else {
          standaloneOptions.push(option);
        }
      });

      setProcessedData({
        features: Array.from(featureMap.values()),
        standaloneOptions,
      });
    }
  }, [product]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };

  /**
   * Renders an indented list of product options with a dash prefix.
   */
  const renderOptionsList = (options) => {
    if (!options || options.length === 0) {
      return (
        <ArgonBox sx={{ pl: 4, py: 2 }}>
          <ArgonTypography variant="body2"> No options available.</ArgonTypography>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox sx={{ pl: 4, py: 1, border: `1px solid ${grey[100]}` }}>
        {options.map((option) => (
          <ArgonBox key={option.sfId || option.id} py={0.75}>
            <ArgonTypography variant="body2" color="secondary" fontWeight="bold">
              {`${option.product?.name || 'N/A'}`}
            </ArgonTypography>
          </ArgonBox>
        ))}
      </ArgonBox>
    );
  };

  /**
   * Renders a single expandable feature card.
   */
  const renderFeatureCard = (feature) => {
    const featureId = feature.sfId || feature.id;
    const isExpanded = expandedRow === featureId;

    return (
      <Card key={featureId} sx={{ mb: 1 }} variant="outlined">
        <ArgonBox
          p={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => handleRowClick(featureId)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonBox display="flex" alignItems="center">
            <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
              {feature.name}
            </ArgonTypography>
          </ArgonBox>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(featureId);
            }}
          >
            <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        </ArgonBox>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {renderOptionsList(feature.options)}
        </Collapse>
      </Card>
    );
  };

  /**
   * Renders a single standalone option as a non-expandable card.
   */
  const renderStandaloneOptionCard = (option) => (
    <Card key={option.sfId || option.id} sx={{ mb: 1 }} variant="outlined">
      <ArgonBox p={1.5} display="flex" alignItems="center">
        <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
          {option.product?.name || 'N/A'}
        </ArgonTypography>
      </ArgonBox>
    </Card>
  );

  const hasContent = processedData.features.length > 0 || processedData.standaloneOptions.length > 0;

  return (
    <ArgonBox p={3}>
      <ArgonTypography variant="h6" mb={2}>
        Features
      </ArgonTypography>
      {!hasContent ? (
        <ArgonBox textAlign="center" py={3}>
          <ArgonTypography variant="body1" color="textSecondary">
            No features or options are configured for this offering.
          </ArgonTypography>
        </ArgonBox>
      ) : (
        <>
          {processedData.features.length > 0 && (
            <ArgonBox>{processedData.features.map((feature) => renderFeatureCard(feature))}</ArgonBox>
          )}
          {processedData.standaloneOptions.length > 0 && (
            <ArgonBox mt={processedData.features.length > 0 ? 4 : 0}>
              <ArgonTypography variant="h6" mb={1}>
                Options
              </ArgonTypography>
              {processedData.standaloneOptions.map((option) => renderStandaloneOptionCard(option))}
            </ArgonBox>
          )}
        </>
      )}
    </ArgonBox>
  );
}

OfferingIncludedProducts.propTypes = {
  product: PropTypes.shape({
    related_data: PropTypes.shape({
      features: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          sfId: PropTypes.string.isRequired,
          name: PropTypes.string,
        })
      ),
      productOptions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          sfId: PropTypes.string.isRequired,
          product: PropTypes.shape({
            name: PropTypes.string,
          }),
          fields: PropTypes.shape({
            SBQQ__Feature__c: PropTypes.string,
          }),
        })
      ),
    }),
  }).isRequired,
};

export default OfferingIncludedProducts;
